import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {IssueFormRef} from '@github-ui/issue-form/Types'
import {readRepositoryFromFragment, type RepositoryPickerRepository} from '@github-ui/item-picker/RepositoryPicker'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {Spinner} from '@primer/react'
import {type RefObject, useCallback, useEffect, useRef} from 'react'
import {fetchQuery, graphql, useFragment, useRelayEnvironment} from 'react-relay'

import type {CreateIssue$key} from './__generated__/CreateIssue.graphql'
import type {CreateIssueQuery} from './__generated__/CreateIssueQuery.graphql'
import {ERRORS} from './constants/errors'
import {useIssueCreateConfigContext} from './contexts/IssueCreateConfigContext'
import {useIssueCreateDataContext} from './contexts/IssueCreateDataContext'
import {CreateDuplicateIssueForm} from './CreateDuplicateIssueForm'
import styles from './CreateIssue.module.css'
import {type CreateIssueCallbackProps, CreateIssueForm} from './CreateIssueForm'
import {useOnCreateSuccess} from './hooks/use-on-create-success'
import type {DuplicateDetectionState} from './hooks/use-similar-issues'
import {RepositoryAndTemplatePicker} from './RepositoryAndTemplatePicker'
import type {TemplateListSharedProps} from './TemplateList'
import {TemplateListInternal} from './TemplateListPane'
import {TemplatePickerButton} from './TemplatePickerButton'
import {getSelectedTemplate, useHandleTemplateChange} from './use-handle-template-change'
import {DisplayMode} from './utils/display-mode'
import {getBlankIssue, IssueCreationKind} from './utils/model'
import type {IssueCreateValueTypes} from './utils/template-args'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const TemplateQuery = graphql`
  query CreateIssueQuery($repoId: ID!, $filename: String!) {
    node(id: $repoId) {
      ... on Repository {
        ...useHandleTemplateChange @dangerously_unaliased_fixme @arguments(filename: $filename)
      }
    }
  }
`

export type CreateIssueProps = {
  currentRepository: CreateIssue$key | null | 'unset'
  topRepositories: RepositoryPickerRepository[] | null | 'unset'
  navigate: (url: string) => void
  issueFormRef: RefObject<IssueFormRef | null>
  onSafeClose?: () => void
  duplicateDetection?: DuplicateDetectionState
} & CreateIssueCallbackProps &
  TemplateListSharedProps

export const CreateIssue = ({
  topRepositories,
  navigate,
  currentRepository,
  onCreateSuccess,
  issueFormRef,
  duplicateDetection,
  ...props
}: CreateIssueProps) => {
  // we're waiting to load the current repo
  // OR we don't have a current repo and we're waiting to load top repos
  const isLoadingRepositoryData =
    (currentRepository !== 'unset' && currentRepository === null) ||
    (topRepositories !== 'unset' && topRepositories === null)

  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment CreateIssue on Repository
      @argumentDefinitions(includeTemplates: {type: "Boolean", defaultValue: false}) {
        ...RepositoryPickerRepository
        ...TemplatePickerButton @dangerously_unaliased_fixme @include(if: $includeTemplates)
        ...TemplateListPane @dangerously_unaliased_fixme @include(if: $includeTemplates)
      }
    `,
    currentRepository !== 'unset' ? currentRepository : null,
  )

  const {addToast} = useToastContext()
  const environment = useRelayEnvironment()
  const {title, body, setBody, repository, setRepository, template, setNewTitle, resetMetadata, clearSessionData} =
    useIssueCreateDataContext()
  const {optionConfig, displayMode, initialDefaultDisplayMode, setDisplayMode, setCreateMoreCreatedPath} =
    useIssueCreateConfigContext()
  const defaultIssueCreateRepoEnabled = isFeatureEnabled('memex_default_issue_create_repository')

  const initialRef = useRef({
    defaultIssueCreateRepoEnabled,
    isInitialRender: true,
    values: {
      resetMetadata,
      initialDefaultDisplayMode,
      repository,
      setDisplayMode,
    },
  })

  useEffect(() => {
    // Only run once on initial render
    if (initialRef.current.isInitialRender) {
      initialRef.current.isInitialRender = false

      if (!initialRef.current.defaultIssueCreateRepoEnabled) return

      const {
        resetMetadata: resetMetadataRef,
        initialDefaultDisplayMode: initialDefaultDisplayModeRef,
        repository: repositoryRef,
        setDisplayMode: setDisplayModeRef,
      } = initialRef.current.values

      if (initialDefaultDisplayModeRef === DisplayMode.IssueDuplication) return

      resetMetadataRef()

      if (repositoryRef?.hasAnyTemplates) {
        setDisplayModeRef(DisplayMode.TemplatePicker)
        return
      }

      if (!repositoryRef?.hasAnyTemplates && repositoryRef?.isBlankIssuesEnabled) {
        setDisplayModeRef(DisplayMode.TemplatePicker)
        return
      }

      // Use initialDefaultDisplayMode as fallback
      setDisplayModeRef(initialDefaultDisplayModeRef)
    }
  }, [])

  const resetDuplicateDetection = duplicateDetection?.resetForNewSession

  const onRepositorySelected = useCallback(
    (selectedRepository: RepositoryPickerRepository | undefined) => {
      if (selectedRepository) {
        if (repository && repository.id !== selectedRepository.id) {
          // We are only clearing metadata because we want to persist title & body across repo changes incase we are selecting blank issues
          resetMetadata({
            repository: initialRef.current.values.repository,
            selectedRepository,
          })

          if (defaultIssueCreateRepoEnabled) {
            if (selectedRepository.hasAnyTemplates) {
              setDisplayMode(DisplayMode.TemplatePicker)
            } else if (!selectedRepository.hasAnyTemplates && selectedRepository.isBlankIssuesEnabled) {
              setDisplayMode(DisplayMode.TemplatePicker)
            }
          }
        }

        setRepository(selectedRepository)
        setCreateMoreCreatedPath({
          owner: selectedRepository.owner.login,
          repo: selectedRepository.name,
          number: undefined,
        })
      }
    },
    [resetMetadata, repository, setCreateMoreCreatedPath, setRepository, setDisplayMode, defaultIssueCreateRepoEnabled],
  )

  const handleTemplateChange = useHandleTemplateChange({
    optionConfig,
    repository,
    navigate,
    setDisplayMode,
  })

  const appendTitleToTemplate = optionConfig.issueCreateArguments?.initialValues?.appendTitleToTemplate

  const onTemplateSelected = useCallback(
    (filename: string, kind: IssueCreationKind) => {
      if (!repository) {
        return
      }

      if (repository?.viewerInteractionLimitReasonHTML && repository.viewerInteractionLimitReasonHTML.length > 0) {
        const redirectUrl = `/${repository.owner.login}/${repository.name}/issues/new`
        navigate(redirectUrl)
        return
      }

      // Contact links will redirect to a new page, so we don't want to set the template
      if (kind === IssueCreationKind.ContactLink) {
        return
      }
      let extraArgs: IssueCreateValueTypes | undefined = undefined
      if (appendTitleToTemplate) {
        extraArgs = {
          appendTitleToTemplate,
        }
      }
      if (kind === IssueCreationKind.BlankIssue) {
        resetDuplicateDetection?.()
        const selectedTemplate = getBlankIssue()
        handleTemplateChange(selectedTemplate, extraArgs, true)
        return
      }

      fetchQuery<CreateIssueQuery>(environment, TemplateQuery, {repoId: repository.id, filename}).subscribe({
        next: response => {
          if (!response.node) {
            return
          }

          const fetchedTemplate = getSelectedTemplate(response.node)

          if (fetchedTemplate) {
            resetDuplicateDetection?.()
            handleTemplateChange(fetchedTemplate, extraArgs, true)
          }
        },
        error: () => {
          reportError(new Error(ERRORS.unableToLoadSelectedTemplate))

          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: ERRORS.unableToLoadSelectedTemplate,
          })
        },
      })
    },
    [repository, environment, appendTitleToTemplate, resetDuplicateDetection, handleTemplateChange, addToast, navigate],
  )

  const onCreateSuccessInternal = useOnCreateSuccess({
    issueFormRef,
    handleTemplateChange,
    template,
    callback: onCreateSuccess,
  })

  useEffect(() => {
    if (repository)
      setCreateMoreCreatedPath({
        owner: repository.owner?.login || '',
        repo: repository.name || '',
        number: undefined,
      })
  }, [repository, setCreateMoreCreatedPath])

  useEffect(() => {
    if (repository !== undefined) return

    if (data) {
      const repo = readRepositoryFromFragment(data)
      onRepositorySelected(repo)
    } else if (
      !isLoadingRepositoryData &&
      topRepositories !== 'unset' &&
      topRepositories &&
      topRepositories.length > 0
    ) {
      onRepositorySelected(topRepositories[0])
    }
  }, [currentRepository, data, isLoadingRepositoryData, onRepositorySelected, repository, topRepositories])

  let element: React.JSX.Element = (
    <div className={styles.LoadingContainer}>
      <Spinner size="large" />
    </div>
  )

  // If we are immediately bypassing the template selection, then we should allow the user to go back to choose another template or repository.
  // We don't allow users to choose another template or repository for issue duplication at the moment
  const shouldShowTemplateButton = initialDefaultDisplayMode === DisplayMode.IssueCreation

  if (displayMode === DisplayMode.IssueCreation && !isLoadingRepositoryData) {
    element = (
      <>
        {shouldShowTemplateButton && data && <TemplatePickerButton repository={data} template={template} />}
        {repository && (
          <CreateIssueForm
            {...props}
            issueFormRef={issueFormRef}
            onCreateSuccess={onCreateSuccessInternal}
            selectedTemplate={template}
            repository={repository}
            title={title}
            setTitle={setNewTitle}
            body={body}
            setBody={setBody}
            clearOnCreate={clearSessionData}
            focusTitleInput={!shouldShowTemplateButton}
            footer={undefined}
            duplicateDetection={duplicateDetection}
          />
        )}
      </>
    )
  } else if (displayMode === DisplayMode.IssueDuplication && !isLoadingRepositoryData) {
    element = (
      <CreateDuplicateIssueForm
        navigate={navigate}
        onCreateSuccess={onCreateSuccessInternal}
        topRepositories={topRepositories}
        issueFormRef={issueFormRef}
        {...props}
      />
    )
  } else if (
    !isLoadingRepositoryData &&
    topRepositories !== 'unset' &&
    ((displayMode === DisplayMode.TemplatePicker && optionConfig.showRepositoryPicker) ||
      (displayMode === DisplayMode.TemplatePicker && !data && !repository))
  ) {
    element = (
      <RepositoryAndTemplatePicker
        repository={repository}
        onRepositorySelected={onRepositorySelected}
        topRepositories={topRepositories}
        organization={optionConfig.scopedOrganization}
        onTemplateSelected={onTemplateSelected}
      />
    )
  } else if (
    displayMode === DisplayMode.TemplatePicker &&
    !optionConfig.showRepositoryPicker &&
    !isLoadingRepositoryData &&
    !!currentRepository &&
    !!data
  ) {
    element = (
      <div data-hpc>
        <TemplateListInternal repository={data} onTemplateSelected={onTemplateSelected} {...props} />
      </div>
    )
  }

  return (
    <div tabIndex={-1} className={styles.CreateIssueContainer}>
      {element}
    </div>
  )
}
